import { Card, Group, Text } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { useFirestoreQuery } from "@/firestore/hooks";
import type { WithDocId } from "@/firestore/types";
import { entityHasDetail, entityPath } from "@/routes/paths";
import type { RelatedSpec } from "@/entities/types";
import { columnsFor } from "@/entities/registry";
import { DataTable } from "./DataTable";
import { isNotYetProjected } from "@/firestore/collections";
import { isPermissionError } from "@/lib/errors";
import { EmptyState, ErrorState, NotProjectedState } from "./states";
import { LedgerRefundAssociations } from "./LedgerRefundAssociations";

/**
 * A titled, live table of entities related to the one being viewed — built by
 * querying the related collection on a denormalized FK field (no server join).
 */
export function RelatedList({ spec }: { spec: RelatedSpec }) {
  const navigate = useNavigate();
  const enabled = spec.value !== undefined && spec.value !== null && spec.value !== "";
  const { data, loading, error } = useFirestoreQuery<Record<string, unknown>>(
    spec.collection,
    {
      filters: enabled ? [[spec.fkField, "==", spec.value]] : [],
      orderBy: spec.orderBy,
      limit: spec.limit ?? 50,
    },
    enabled,
  );

  const clickable = entityHasDetail(spec.entity);

  return (
    <Card withBorder radius="md" padding="md">
      <Group justify="space-between" mb="xs">
        <Text fw={600}>{spec.title}</Text>
        {!loading && !error ? (
          <Text size="sm" c="dimmed">
            {data.length}
          </Text>
        ) : null}
      </Group>
      {error ? (
        // Not-yet-projected collections have no rules deployed → the query is
        // denied; show the explanatory empty state instead of an error.
        isNotYetProjected(spec.collection) && isPermissionError(error) ? (
          <NotProjectedState />
        ) : (
          <ErrorState error={error} />
        )
      ) : (
        <LedgerRefundAssociations
          ledgerIds={
            spec.entity === "ledgerEntry"
              ? data.map((r) => String(r.id ?? r._docId))
              : spec.entity === "walletTransaction"
                ? data.flatMap((r) => (typeof r.ledgerEntry === "string" ? [r.ledgerEntry] : []))
                : []
          }
        >
          <DataTable
            data={data}
            columns={columnsFor(spec.entity)}
            loading={loading}
            searchable={false}
            onRowClick={
              clickable
                ? (row) => navigate(entityPath(spec.entity, (row as WithDocId<unknown>)._docId))
                : undefined
            }
            emptyState={<EmptyState title="None" description={`No ${spec.title.toLowerCase()}.`} />}
          />
        </LedgerRefundAssociations>
      )}
    </Card>
  );
}
